import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
  updateMyListing,
  type OwnerListing,
  type ListingUpdatePayload,
} from "@/services/ownerService";
import { ApiError } from "@/services/http";
import { getPlanLimits } from "@/utils/plans";
import ImageUploader from "@/components/base/ImageUploader";
import MediaLibraryModal from "@/components/feature/MediaLibraryModal";
import {
  LISTING_COVER_RULE,
  LISTING_GALLERY_RULE,
  LISTING_LOGO_RULE,
} from "@/utils/imageValidation";

interface ListingEditorProps {
  listing: OwnerListing;
  onClose: () => void;
  onSaved: (id: number, payload: ListingUpdatePayload) => void;
}

const inputClass =
  "h-11 w-full rounded-lg border border-background-300 bg-background-50 px-4 text-sm text-foreground-800 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100";
const labelClass = "mb-1.5 block text-xs font-semibold uppercase tracking-wider text-foreground-600";

export default function ListingEditor({ listing, onClose, onSaved }: ListingEditorProps) {
  const { accessToken, isDemoMode } = useAuth();

  const [form, setForm] = useState<ListingUpdatePayload>({
    name:        listing.name,
    tagline:     listing.tagline ?? "",
    description: listing.description ?? "",
    address:     listing.address ?? "",
    phone:       listing.phone ?? "",
    whatsapp:    listing.whatsapp ?? "",
    email:       listing.email ?? "",
    website:     listing.website ?? "",
    videoUrl:    listing.videoUrl ?? "",
    coverUrl:    listing.coverUrl ?? "",
    logoUrl:     listing.logoUrl ?? "",
    gallery:     listing.gallery ?? [],
  });
  const [error,      setError]      = useState("");
  const [submitting, setSubmitting] = useState(false);
  /** Which image field the Media Library picker is currently filling. */
  const [picker,     setPicker]     = useState<null | "cover" | "logo" | "gallery">(null);

  // Photo cap and video access come straight from the listing's plan, so the
  // editor can never allow more than the plan pays for.
  const limits = getPlanLimits(listing.planSlug);
  const photoLimit = Math.max(1, limits.photos);
  const canVideo = limits.video;

  function set<K extends keyof ListingUpdatePayload>(key: K, value: string) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function setImages(images: string[]) {
    setForm((prev) => ({ ...prev, gallery: images }));
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError("");
    if (!form.name?.trim()) { setError("Business name cannot be empty."); return; }

    setSubmitting(true);
    try {
      await updateMyListing(accessToken, listing.id, form);
      onSaved(listing.id, form);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not save changes. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  const willResubmit = listing.status === "rejected";

  return (
    <>
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-foreground-950/50 p-4" onClick={onClose}>
      <div
        className="flex max-h-[90vh] w-full max-w-lg flex-col overflow-hidden rounded-xl border border-background-200 bg-background-50"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-background-200 px-6 py-4">
          <div>
            <h2 className="font-heading text-base font-bold text-foreground-950">Edit listing</h2>
            <p className="mt-0.5 text-xs text-foreground-500">{listing.name}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md text-foreground-500 transition-colors hover:bg-background-100 hover:text-foreground-900"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="flex flex-col gap-4 overflow-y-auto px-6 py-5">
          {willResubmit && (
            <div className="flex items-start gap-2.5 rounded-lg border border-accent-200 bg-accent-50 px-4 py-3 text-xs text-accent-800">
              <i className="ri-refresh-line mt-0.5 shrink-0 text-sm" />
              Saving your changes will resubmit this listing for review.
            </div>
          )}

          <div>
            <label className={labelClass} htmlFor="ed-name">Business name *</label>
            <input id="ed-name" type="text" required value={form.name ?? ""}
              onChange={(e) => set("name", e.target.value)} className={inputClass} />
          </div>

          <div>
            <label className={labelClass} htmlFor="ed-tagline">Tagline</label>
            <input id="ed-tagline" type="text" value={form.tagline ?? ""}
              onChange={(e) => set("tagline", e.target.value)}
              placeholder="A short line that describes your business" className={inputClass} />
          </div>

          <div>
            <label className={labelClass} htmlFor="ed-desc">About your business</label>
            <textarea id="ed-desc" rows={4} maxLength={1000} value={form.description ?? ""}
              onChange={(e) => set("description", e.target.value)}
              placeholder="Tell customers what you offer and what makes you different..."
              className="w-full resize-none rounded-lg border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-800 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100" />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className={labelClass} htmlFor="ed-phone">Phone</label>
              <input id="ed-phone" type="tel" value={form.phone ?? ""}
                onChange={(e) => set("phone", e.target.value)} className={inputClass} />
            </div>
            <div>
              <label className={labelClass} htmlFor="ed-whatsapp">WhatsApp</label>
              <input id="ed-whatsapp" type="tel" value={form.whatsapp ?? ""}
                onChange={(e) => set("whatsapp", e.target.value)} className={inputClass} />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className={labelClass} htmlFor="ed-email">Email</label>
              <input id="ed-email" type="email" value={form.email ?? ""}
                onChange={(e) => set("email", e.target.value)} className={inputClass} />
            </div>
            <div>
              <label className={labelClass} htmlFor="ed-website">Website</label>
              <input id="ed-website" type="url" value={form.website ?? ""}
                onChange={(e) => set("website", e.target.value)}
                placeholder="https://" className={inputClass} />
            </div>
          </div>

          <div>
            <label className={labelClass} htmlFor="ed-address">Address</label>
            <input id="ed-address" type="text" value={form.address ?? ""}
              onChange={(e) => set("address", e.target.value)} className={inputClass} />
          </div>

          <div className="rounded-lg border border-background-200 bg-background-100/50 p-4">
            <div className="mb-3">
              <h3 className="font-heading text-sm font-bold text-foreground-950">Photos</h3>
              <p className="mt-0.5 text-xs text-foreground-500">
                Validated on the client and the server — JPG, PNG or WebP only.
              </p>
            </div>

            <div className="flex flex-col gap-4">
              <div>
                <span className={labelClass}>Cover image</span>
                <ImageUploader
                  value={form.coverUrl ? [form.coverUrl] : []}
                  onChange={(urls) => set("coverUrl", urls[0] ?? "")}
                  rule={LISTING_COVER_RULE}
                  folder="listings"
                  token={accessToken}
                  onBrowseLibrary={() => setPicker("cover")}
                />
              </div>

              <div>
                <span className={labelClass}>Logo</span>
                <ImageUploader
                  value={form.logoUrl ? [form.logoUrl] : []}
                  onChange={(urls) => set("logoUrl", urls[0] ?? "")}
                  rule={LISTING_LOGO_RULE}
                  folder="logos"
                  token={accessToken}
                  onBrowseLibrary={() => setPicker("logo")}
                />
              </div>

              <div>
                <span className={labelClass}>Gallery ({ (form.gallery ?? []).length }/{photoLimit})</span>
                <ImageUploader
                  value={form.gallery ?? []}
                  onChange={setImages}
                  rule={LISTING_GALLERY_RULE}
                  folder="gallery"
                  token={accessToken}
                  multiple
                  maxFiles={photoLimit}
                  onBrowseLibrary={() => setPicker("gallery")}
                  browseLabel="Add from library"
                  capacityMessage={
                    <>
                      You&apos;ve reached your plan limit of {photoLimit}{" "}
                      {photoLimit === 1 ? "photo" : "photos"}.{" "}
                      <Link
                        to="/listing-plans"
                        className="font-semibold text-primary-700 hover:underline"
                      >
                        Upgrade for more
                      </Link>
                    </>
                  }
                />
                <p className="mt-1 text-xs text-foreground-500">
                  Your plan allows up to {photoLimit} {photoLimit === 1 ? "photo" : "photos"}. Cover and logo are counted separately.
                </p>
              </div>
            </div>
          </div>

          <div>
            <label className={labelClass} htmlFor="ed-video">Video tour</label>
            <div className="relative">
              <input
                id="ed-video"
                type="url"
                value={form.videoUrl ?? ""}
                disabled={!canVideo}
                onChange={(e) => set("videoUrl", e.target.value)}
                placeholder="https://www.youtube.com/watch?v=..."
                className={`${inputClass} ${!canVideo ? "cursor-not-allowed bg-background-100 pr-10 text-foreground-500" : ""}`}
              />
              {!canVideo ? (
                <span className="pointer-events-none absolute right-3 top-1/2 flex h-4 w-4 -translate-y-1/2 items-center justify-center text-foreground-400">
                  <i className="ri-lock-line text-base" />
                </span>
              ) : null}
            </div>
            {canVideo ? (
              <p className="mt-1 text-xs text-foreground-500">
                Paste a YouTube, Vimeo or other streaming link. It shows as a “Video Tour” widget on your listing page.
              </p>
            ) : (
              <p className="mt-1 flex flex-wrap items-center gap-x-1.5 gap-y-1 text-xs text-foreground-500">
                <span className="flex items-center gap-1.5">
                  <i className="ri-lock-line text-foreground-400" />
                  Your current plan does not include a video tour.
                </span>
                <Link to="/listing-plans" className="font-semibold text-primary-700 hover:underline">
                  Compare plans
                </Link>
              </p>
            )}
          </div>

          {error && (
            <div className="flex items-start gap-2.5 rounded-lg bg-rose-50 px-4 py-3 text-sm text-rose-700">
              <i className="ri-error-warning-line mt-0.5 shrink-0 text-base" />{error}
            </div>
          )}

          {isDemoMode && (
            <p className="text-xs text-foreground-500">
              <i className="ri-information-line mr-1" />
              Demo mode — changes are previewed locally and not saved to a server.
            </p>
          )}

          <div className="mt-1 flex flex-col gap-2 sm:flex-row sm:justify-end">
            <button type="button" onClick={onClose}
              className="inline-flex h-11 items-center justify-center whitespace-nowrap rounded-lg border border-background-300 px-5 text-sm font-semibold text-foreground-700 transition-colors hover:bg-background-100">
              Cancel
            </button>
            <button type="submit" disabled={submitting}
              className="inline-flex h-11 items-center justify-center gap-2 whitespace-nowrap rounded-lg bg-primary-500 px-6 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60">
              {submitting
                ? <><i className="ri-loader-4-line animate-spin text-base" /> Saving…</>
                : <><i className="ri-save-line text-base" /> Save changes</>}
            </button>
          </div>
        </form>
      </div>
    </div>

      {picker && (
        <MediaLibraryModal
          token={accessToken}
          canUpload
          uploadFolder={picker === "cover" ? "listings" : picker === "logo" ? "logos" : "gallery"}
          title={picker === "gallery" ? "Add from Media Library" : "Choose from Media Library"}
          multiple={picker === "gallery"}
          maxSelect={picker === "gallery" ? Math.max(1, photoLimit - (form.gallery ?? []).length) : 1}
          currentUrls={
            picker === "cover"
              ? form.coverUrl ? [form.coverUrl] : []
              : picker === "logo"
                ? form.logoUrl ? [form.logoUrl] : []
                : form.gallery ?? []
          }
          onSelect={(urls) => {
            if (picker === "cover") set("coverUrl", urls[0] ?? "");
            else if (picker === "logo") set("logoUrl", urls[0] ?? "");
            else {
              const existing = form.gallery ?? [];
              const merged = [...existing];
              for (const u of urls) if (!merged.includes(u)) merged.push(u);
              setImages(merged.slice(0, photoLimit));
            }
            setPicker(null);
          }}
          onClose={() => setPicker(null)}
        />
      )}
    </>
  );
}